import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export const Navigation = ({ activeSection, onNavigate }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();

  const menuItems = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'Working Experience' },
    { id: 'certifications', label: 'My Certification' },
    { id: 'bugs', label: 'Production Bug Findings' },
    { id: 'contact', label: 'Contact' },
  ];

  const handleNavClick = (id) => {
    onNavigate(id);
    setMobileMenuOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="fixed top-0 left-0 right-0 z-50 bg-[#0B1910]/70 backdrop-blur-2xl border-b border-white/5"
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-emerald-600 rounded-full"></div>
            <span className="text-white font-medium text-lg" style={{ fontFamily: 'Outfit' }}>
              Melvin's Portfolio
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                data-testid={`nav-${item.id}`}
                className={`text-sm tracking-wide transition-all duration-300 ease-out ${
                  activeSection === item.id
                    ? 'text-white font-medium'
                    : 'text-slate-400 hover:text-white'
                }`}
                style={{ fontFamily: 'IBM Plex Sans' }}
              >
                {item.label}
              </button>
            ))}
            {user && (
              <button
                onClick={logout}
                className="text-sm tracking-wide text-slate-400 hover:text-white transition-all duration-300 ease-out flex items-center space-x-2"
                style={{ fontFamily: 'IBM Plex Sans' }}
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2 hover:bg-slate-800/40 rounded-sm transition-colors duration-200"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="md:hidden bg-[#0B1910] border-t border-white/5"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-4 space-y-1">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                data-testid={`mobile-nav-${item.id}`}
                className={`w-full text-left px-4 py-3 rounded-sm transition-all duration-300 ease-out ${
                  activeSection === item.id
                    ? 'text-white font-medium bg-slate-800/40'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/20'
                }`}
                style={{ fontFamily: 'IBM Plex Sans' }}
              >
                {item.label}
              </button>
            ))}
            {user && (
              <button
                onClick={() => {
                  logout();
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-4 py-3 rounded-sm text-slate-400 hover:text-white hover:bg-slate-800/20 transition-all duration-300 ease-out flex items-center space-x-2"
                style={{ fontFamily: 'IBM Plex Sans' }}
                data-testid="mobile-logout-button"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            )}
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};
